import { getExtensions } from '@/components/tiptap-editor/tiptap-editor';
import { migrateZybookMathStrings } from '@/components/tiptap-editor/tiptap-extension/math-extension';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { Tag } from '@/db/schema';
import { tagsDb } from '@/db/tags';
import { extractUICriticalFieldsFromArray, getSnippetsForTag, sortObjectKeys } from '@/lib/utils';
import { zyxmlToJson } from '@/lib/zybooks-api';
import { convertParseResponseSection } from '@/lib/zybooks-parser/from-zyxml';
import { convertProseMirrorToZybooksJSON } from '@/lib/zybooks-parser/to-zybooks-json';
import { Editor } from '@tiptap/react';
import { useLiveQuery } from 'dexie-react-hooks';
import { diffJson, type Change } from 'diff';
import { CheckCircle2, ChevronDown, ChevronRight, LoaderCircle, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';

export interface DiffResult {
    snippet: string;
    status: 'success' | 'failed';
    error?: string;
    apiResult?: any[];
    maResult?: any[];
    diffResult?: Change[];
    index: number;
}

const NULLABLE_DIFF_ATTRS = ['activity_type', 'caption', 'height', 'width', 'instructions'] as const;

export function dropNullKeysOnMAWhenApiKeyMissing(api: any[], ma: any[]): { api: any[]; ma: any[] } {
    if (!Array.isArray(api) || !Array.isArray(ma)) return { api, ma };

    const maNormalized = ma.map((maObj, idx) => {
        if (!maObj || typeof maObj !== 'object' || Array.isArray(maObj)) return maObj;

        const apiObj = api[idx];
        if (!apiObj || typeof apiObj !== 'object' || Array.isArray(apiObj)) {
            return maObj;
        }

        const cleaned: Record<string, any> = { ...maObj };
        for (const key of NULLABLE_DIFF_ATTRS) {
            if (
                Object.prototype.hasOwnProperty.call(cleaned, key) &&
                cleaned[key] === null &&
                !Object.prototype.hasOwnProperty.call(apiObj, key)
            ) {
                delete cleaned[key];
            }
        }
        return cleaned;
    });

    return { api, ma: maNormalized };
}

export function isFootnoteReferenceHtmlResource(resource: any): boolean {
    if (!resource || typeof resource !== 'object') return false;
    if (resource.type !== 'html') return false;
    const htmlText = resource.payload?.html?.[0]?.text;
    return typeof htmlText === 'string' && htmlText.includes('footnote-reference');
}

export function isEmptyHtmlParagraph(htmlText: unknown): boolean {
    if (typeof htmlText !== 'string') return false;

    const trimmed = htmlText.trim();
    if (!trimmed) return true;

    const doc = new DOMParser().parseFromString(trimmed, 'text/html');
    const body = doc.body;
    if (!body || body.children.length !== 1) return false;

    const el = body.children[0];
    if (el.tagName.toLowerCase() !== 'p') return false;

    for (const child of Array.from(el.children)) {
        if (child.tagName.toLowerCase() !== 'br') return false;
    }

    const text = (el.textContent ?? '').replace(/[\s\u00A0\u200B]/g, '');
    return text.length === 0;
}

export function stripTrailingMAEmptyParagraphForDiff(resources: any[]): any[] {
    if (!Array.isArray(resources) || resources.length === 0) return resources;

    let idx = resources.length - 1;
    while (idx >= 0 && isFootnoteReferenceHtmlResource(resources[idx])) {
        idx -= 1;
    }
    if (idx < 0) return resources;

    const candidate = resources[idx];
    const candidateHtmlText = candidate?.payload?.html?.[0]?.text;
    if (candidate?.type === 'html' && isEmptyHtmlParagraph(candidateHtmlText)) {
        return [...resources.slice(0, idx), ...resources.slice(idx + 1)];
    }

    return resources;
}

export function wrapSnippetIfNeeded(input: string): string {
    const trimmed = input.trim();
    if (trimmed.includes('<zyChapter') || trimmed.includes('<zychapter')) {
        return trimmed;
    }
    return `<zyChapter title="title" shorttitle="shorttitle" chapter="1">
    <zySection title="title" shorttitle="shorttitle" id="00000000-0000-0000-0000-000000000000" zymath="true">
        ${trimmed}
    </zySection>
</zyChapter>`;
}

export async function runDiffForSnippet(snippet: string, index: number): Promise<DiffResult> {
    const wrappedInput = wrapSnippetIfNeeded(snippet);

    try {
        let apiResponse;
        let maConvertResult;
        let proseMirrorJson;

        try {
            apiResponse = await zyxmlToJson(wrappedInput);
            if (apiResponse.error || apiResponse.errors) {
                throw new Error(apiResponse.error?.message || 'API conversion failed');
            }
            if (!apiResponse.sections || apiResponse.sections.length === 0) {
                throw new Error('No sections found in API response');
            }
        } catch (err) {
            return {
                snippet,
                status: 'failed',
                error: `API conversion failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
                index,
            };
        }

        try {
            maConvertResult = await convertParseResponseSection(apiResponse.sections[0], 'json', {
                devBookName: '',
                subject: '',
                releaseId: '',
            });
            if (!maConvertResult) {
                throw new Error('convertParseResponseSection returned null');
            }

            const editor = new Editor({
                element: document.createElement('div'),
                extensions: getExtensions(),
                content: maConvertResult.output,
            });

            migrateZybookMathStrings(editor);
            proseMirrorJson = editor.getJSON();
            editor.destroy();
        } catch (err) {
            return {
                snippet,
                status: 'failed',
                error: `MA conversion failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
                index,
            };
        }

        try {
            const apiContentResources = apiResponse.sections[0].content_resources;

            const sectionId = apiResponse.sections[0].id || '00000000-0000-0000-0000-000000000000';
            if (!proseMirrorJson.attrs) {
                proseMirrorJson.attrs = {};
            }
            proseMirrorJson.attrs.sectionId = sectionId;

            const { contentResources: maContentResources } = await convertProseMirrorToZybooksJSON(proseMirrorJson);
            const maContentResourcesForDiff = stripTrailingMAEmptyParagraphForDiff(maContentResources);

            const apiUICriticalRaw = extractUICriticalFieldsFromArray(apiContentResources);
            const maUICriticalRaw = extractUICriticalFieldsFromArray(maContentResourcesForDiff);
            const { api: apiUICritical, ma: maUICritical } = dropNullKeysOnMAWhenApiKeyMissing(apiUICriticalRaw, maUICriticalRaw);
            const sortedApi = sortObjectKeys(apiUICritical);
            const sortedMA = sortObjectKeys(maUICritical);
            const diff = diffJson(sortedApi, sortedMA);

            return {
                snippet,
                status: 'success',
                apiResult: apiContentResources,
                maResult: maContentResources,
                diffResult: diff,
                index,
            };
        } catch (err) {
            return {
                snippet,
                status: 'failed',
                error: `Diff computation failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
                index,
            };
        }
    } catch (err) {
        return {
            snippet,
            status: 'failed',
            error: `Unexpected error: ${err instanceof Error ? err.message : 'Unknown error'}`,
            index,
        };
    }
}

export default function BatchDiffContent() {
    const tags = useLiveQuery<Tag[]>(() => tagsDb.tags.toArray(), []);
    const [selectedTag, setSelectedTag] = useState<string>('');
    const [isRunning, setIsRunning] = useState(false);
    const [results, setResults] = useState<DiffResult[]>([]);
    const [currentProgress, setCurrentProgress] = useState<{ current: number; total: number } | null>(null);
    const [expandedResults, setExpandedResults] = useState<Set<number>>(new Set());

    const zyTags = useMemo(() => {
        if (!tags) return [];
        return tags.filter(tag => tag.id.toLowerCase().startsWith('zy')).sort((a, b) => a.id.localeCompare(b.id));
    }, [tags]);

    const handleRunDiff = async () => {
        if (!selectedTag) return;

        setIsRunning(true);
        setResults([]);
        setCurrentProgress(null);

        try {
            const entries = await getSnippetsForTag(selectedTag, () => tagsDb.relationshipTags.toArray(), 10);
            if (entries.length === 0) {
                setResults([
                    {
                        snippet: '',
                        status: 'failed',
                        error: 'No snippets found for this tag',
                        index: 0,
                    },
                ]);
                setIsRunning(false);
                return;
            }

            const newResults: DiffResult[] = [];
            setCurrentProgress({ current: 0, total: entries.length });

            for (let i = 0; i < entries.length; i++) {
                setCurrentProgress({ current: i + 1, total: entries.length });
                const result = await runDiffForSnippet(entries[i].snippet, i);
                newResults.push(result);
                setResults([...newResults]);
            }
        } catch (err) {
            console.error('Batch diff error:', err);
        } finally {
            setIsRunning(false);
            setCurrentProgress(null);
        }
    };

    const toggleResult = (index: number) => {
        const newExpanded = new Set(expandedResults);
        if (newExpanded.has(index)) {
            newExpanded.delete(index);
        } else {
            newExpanded.add(index);
        }
        setExpandedResults(newExpanded);
    };

    const successCount = results.filter(r => r.status === 'success').length;
    const failedCount = results.filter(r => r.status === 'failed').length;

    return (
        <div className="flex h-svh flex-col">
            <header className="flex h-14 items-center border-b px-4">
                <h1 className="text-lg font-semibold">Batch Diff Comparison</h1>
            </header>
            <div className="flex flex-1 flex-col gap-4 overflow-hidden p-4">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <Label htmlFor="tag-select">Select Tag:</Label>
                        <Select
                            value={selectedTag}
                            onValueChange={setSelectedTag}
                            disabled={isRunning}
                        >
                            <SelectTrigger
                                id="tag-select"
                                className="w-48"
                            >
                                <SelectValue placeholder="Select a zyTag" />
                            </SelectTrigger>
                            <SelectContent>
                                {zyTags.map(tag => (
                                    <SelectItem
                                        key={tag.id}
                                        value={tag.id}
                                    >
                                        {tag.id}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button
                        onClick={handleRunDiff}
                        disabled={!selectedTag || isRunning}
                    >
                        {isRunning ? (
                            <>
                                <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                                {currentProgress ? `Processing ${currentProgress.current}/${currentProgress.total}...` : 'Running...'}
                            </>
                        ) : (
                            'Run Diff'
                        )}
                    </Button>
                    {results.length > 0 && (
                        <div className="ml-auto text-sm text-zinc-600">
                            {successCount} succeeded, {failedCount} failed
                        </div>
                    )}
                </div>

                <div className="flex-1 overflow-auto">
                    {results.length === 0 && !isRunning && (
                        <div className="flex h-full items-center justify-center text-zinc-500">
                            Select a tag and click "Run Diff" to start batch comparison
                        </div>
                    )}

                    {results.length > 0 && (
                        <div className="space-y-4">
                            {results.map((result, idx) => {
                                const isExpanded = expandedResults.has(result.index);
                                const hasDifferences = result.diffResult && result.diffResult.some(part => part.added || part.removed);

                                return (
                                    <Card
                                        key={idx}
                                        className={result.status === 'failed' ? 'border-red-200' : ''}
                                    >
                                        <Collapsible
                                            open={isExpanded}
                                            onOpenChange={() => toggleResult(result.index)}
                                        >
                                            <CollapsibleTrigger className="w-full">
                                                <CardHeader className="flex flex-row items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        {isExpanded ? (
                                                            <ChevronDown className="h-4 w-4" />
                                                        ) : (
                                                            <ChevronRight className="h-4 w-4" />
                                                        )}
                                                        <CardTitle className="text-base">Result {result.index + 1}</CardTitle>
                                                        {result.status === 'success' ? (
                                                            <span className="flex items-center gap-1 text-sm text-emerald-600">
                                                                <CheckCircle2 className="h-4 w-4" />
                                                                Success
                                                                {hasDifferences && (
                                                                    <span className="text-amber-600">(Differences found)</span>
                                                                )}
                                                                {!hasDifferences && (
                                                                    <span className="text-emerald-600">(No differences)</span>
                                                                )}
                                                            </span>
                                                        ) : (
                                                            <span className="flex items-center gap-1 text-sm text-red-600">
                                                                <XCircle className="h-4 w-4" />
                                                                Failed: {result.error}
                                                            </span>
                                                        )}
                                                    </div>
                                                </CardHeader>
                                            </CollapsibleTrigger>
                                            <CollapsibleContent>
                                                <CardContent className="space-y-4">
                                                    <div className="flex flex-col gap-2">
                                                        <div className="text-sm font-medium">XML Snippet:</div>
                                                        <pre className="max-h-48 overflow-auto rounded-md border bg-zinc-50 p-3 text-xs font-mono">
                                                            {result.snippet}
                                                        </pre>
                                                    </div>
                                                    {result.status === 'success' && result.diffResult && (
                                                        <div className="flex flex-col gap-2 border-t pt-4">
                                                            <div className="text-sm font-medium">Diff Details (Side-by-Side):</div>
                                                            <div className="flex gap-4 h-64 overflow-hidden">
                                                                <div className="flex-1 flex flex-col gap-1 overflow-hidden">
                                                                    <div className="text-xs font-medium text-zinc-600">
                                                                        zyAuthor API (removed/changed in red):
                                                                    </div>
                                                                    <div className="flex-1 overflow-auto rounded-md border bg-zinc-50 p-4 text-xs font-mono whitespace-pre">
                                                                        {result.diffResult.map((part, partIdx) => {
                                                                            if (part.added) return null;
                                                                            const bgColor = part.removed ? 'bg-rose-100' : 'bg-transparent';
                                                                            const textColor = part.removed
                                                                                ? 'text-rose-800'
                                                                                : 'text-zinc-700';
                                                                            return (
                                                                                <span
                                                                                    key={partIdx}
                                                                                    className={`${bgColor} ${textColor}`}
                                                                                >
                                                                                    {part.value}
                                                                                </span>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                                <div className="flex-1 flex flex-col gap-1 overflow-hidden">
                                                                    <div className="text-xs font-medium text-zinc-600">
                                                                        MA Path (added/changed in green):
                                                                    </div>
                                                                    <div className="flex-1 overflow-auto rounded-md border bg-zinc-50 p-4 text-xs font-mono whitespace-pre">
                                                                        {result.diffResult.map((part, partIdx) => {
                                                                            if (part.removed) return null;
                                                                            const bgColor = part.added
                                                                                ? 'bg-emerald-100'
                                                                                : 'bg-transparent';
                                                                            const textColor = part.added
                                                                                ? 'text-emerald-800'
                                                                                : 'text-zinc-700';
                                                                            return (
                                                                                <span
                                                                                    key={partIdx}
                                                                                    className={`${bgColor} ${textColor}`}
                                                                                >
                                                                                    {part.value}
                                                                                </span>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </CardContent>
                                            </CollapsibleContent>
                                        </Collapsible>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
